/**
 * Single owner of the `referral_code` localStorage entry.
 *
 * The key name predates this module (sign-in and sign-up wrote it directly), so it is
 * kept for compatibility with codes already stored in visitors' browsers.
 *
 * Same-tab changes do not fire the browser `storage` event, so writers notify an
 * in-module listener set; `subscribeReferralCode` also listens to the cross-tab
 * `storage` event when a window exists.
 */
export const REFERRAL_CODE_STORAGE_KEY = 'referral_code';

export type ReferralStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const listeners = new Set<() => void>();

const defaultStorage = (): ReferralStorage | null => (typeof localStorage === 'undefined' ? null : localStorage);

const notify = () => listeners.forEach(listener => listener());

export const getReferralCode = (storage: ReferralStorage | null = defaultStorage()): string | null => {
  try {
    return storage?.getItem(REFERRAL_CODE_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
};

export const storeReferralCode = (code: string, storage: ReferralStorage | null = defaultStorage()): void => {
  try {
    storage?.setItem(REFERRAL_CODE_STORAGE_KEY, code);
  } catch {
    // Storage blocked: the branding simply does not persist.
  }
  notify();
};

export const clearReferralCode = (storage: ReferralStorage | null = defaultStorage()): void => {
  try {
    storage?.removeItem(REFERRAL_CODE_STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is blocked.
  }
  notify();
};

export const subscribeReferralCode = (listener: () => void): (() => void) => {
  listeners.add(listener);

  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === REFERRAL_CODE_STORAGE_KEY) listener();
  };
  const hasWindow = typeof window !== 'undefined';
  if (hasWindow) window.addEventListener('storage', onStorage);

  return () => {
    listeners.delete(listener);
    if (hasWindow) window.removeEventListener('storage', onStorage);
  };
};
